import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { apiService, ApiError } from '../../services/api';
import {
  Dispute,
  EditDisputeRequest,
  Bureau,
  PersonalInfoCategory,
  AccountInformation,
  CreditInquiry,
  PersonalInformation,
} from '../../types/api';
import { Loader2, Check, CreditCard, Info, Pencil } from 'lucide-react';

type DisputeType = 'account' | 'inquiry' | 'late' | 'personal-info';

interface UserDocument {
  id: string;
  userId: string;
  documentName: string;
  fileSize: number;
  mimeType: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

interface EditDisputeDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onDisputeUpdated: (updatedDispute: Dispute) => void;
  dispute: Dispute;
  accounts: AccountInformation[];
  accountsGroupedById: Map<string, AccountInformation[]>;
  availableInquiries: CreditInquiry[];
  documents: UserDocument[];
  personalInformation: PersonalInformation[];
}

function inferDisputeType(dispute: Dispute): DisputeType {
  const t = (dispute.type || dispute.title || '').toLowerCase();
  if (t.includes('personal') || t.includes('info')) return 'personal-info';
  if (t.includes('inquiry')) return 'inquiry';
  if (t.includes('late')) return 'late';
  return 'account';
}

const NON_EDITABLE_STATUSES = ['mailed', 'in_transit', 'delivered', 'success'];

export function isDisputeEditable(
  dispute: Dispute,
  timelineLetters?: Array<{ letter_id: string; status: string }>
): boolean {
  if (!timelineLetters || timelineLetters.length === 0) return true;
  return timelineLetters.every(tl =>
    !NON_EDITABLE_STATUSES.includes(tl.status)
  );
}

export function EditDisputeDialog({
  isOpen,
  onOpenChange,
  onDisputeUpdated,
  dispute,
  accounts,
  accountsGroupedById,
  availableInquiries,
  documents,
  personalInformation = [],
}: EditDisputeDialogProps) {
  const disputeType = inferDisputeType(dispute);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [isLoadingDispute, setIsLoadingDispute] = useState(false);
  const [freshDispute, setFreshDispute] = useState<Dispute | null>(null);

  // Form state
  const [reason, setReason] = useState('');
  const [supportingReason, setSupportingReason] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);

  // Account / Lates state
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [expandedAccountId, setExpandedAccountId] = useState<string | null>(null);
  const [selectedBureaus, setSelectedBureaus] = useState<Record<string, Bureau[]>>({});

  // Inquiry state
  const [selectedInquiryIds, setSelectedInquiryIds] = useState<string[]>([]);

  // Personal Info state
  const [expandedPersonalInfoField, setExpandedPersonalInfoField] = useState<string | null>(null);
  const [selectedPersonalInfoBureaus, setSelectedPersonalInfoBureaus] = useState<Record<string, Bureau[]>>({});

  // Account info popup
  const [showAccountInfo, setShowAccountInfo] = useState<string | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ top: number; right: number } | null>(null);
  const infoButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  // Resolve a rawRequest account to an analytics account.
  // The rawRequest stores the account id at time of creation; the analytics
  // list may use a different id scheme, so we fall back to matching by
  // creditorName + accountNumber.
  const resolveAccountId = (rawAccount: { id: string; creditorName?: string; accountNumber?: string }): string => {
    if (accounts.find(a => a.id === rawAccount.id)) return rawAccount.id;
    const byName = accounts.find(a =>
      a.creditorName === rawAccount.creditorName && a.accountNumber === rawAccount.accountNumber
    );
    return byName?.id || rawAccount.id;
  };

  // Similarly for inquiries
  const resolveInquiryId = (rawInquiry: { id: string; company?: string; date?: string }): string => {
    if (availableInquiries.find(i => i.id === rawInquiry.id)) return rawInquiry.id;
    const byFields = availableInquiries.find(i =>
      i.company === rawInquiry.company && i.date === rawInquiry.date
    );
    return byFields?.id || rawInquiry.id;
  };

  // Pre-populate from rawRequest on open, fetching fresh data from API
  useEffect(() => {
    if (!isOpen) {
      setFreshDispute(null);
      return;
    }
    setError('');

    // Reset all selection state
    setReason('');
    setSupportingReason('');
    setSelectedDocuments([]);
    setSelectedAccountId('');
    setExpandedAccountId(null);
    setSelectedBureaus({});
    setSelectedInquiryIds([]);
    setExpandedPersonalInfoField(null);
    setSelectedPersonalInfoBureaus({});

    let cancelled = false;

    const populateFrom = (d: Dispute) => {
      if (cancelled) return;
      const req = d.rawRequest || {};

      setReason(req.reason || '');
      setSupportingReason(req.supportingReason || d.supportingReason || '');
      setSelectedDocuments(req.attachmentNames || d.attachmentNames || []);

      setSelectedAccountId('');
      setExpandedAccountId(null);
      setSelectedBureaus({});
      setSelectedInquiryIds([]);
      setExpandedPersonalInfoField(null);
      setSelectedPersonalInfoBureaus({});

      const type = inferDisputeType(d);

      if (type === 'account' || type === 'late') {
        const bureauDisputes: Array<{ bureau: string; account: { id: string; creditorName?: string; accountNumber?: string } }> = req.bureauAccountDisputes || [];
        if (bureauDisputes.length > 0) {
          const rawAccount = bureauDisputes[0].account;
          const resolvedId = resolveAccountId(rawAccount);
          setSelectedAccountId(resolvedId);
          setExpandedAccountId(resolvedId);
          const bureauMap: Record<string, Bureau[]> = {};
          bureauMap[resolvedId] = bureauDisputes.map(bd => bd.bureau as Bureau);
          setSelectedBureaus(bureauMap);
        }
      } else if (type === 'inquiry') {
        const inqDisputes: Array<{ bureau: string; inquiry: { id: string; company?: string; date?: string } }> = req.bureauInquiryDisputes || [];
        setSelectedInquiryIds(
          inqDisputes.map(bd => resolveInquiryId(bd.inquiry)).filter(Boolean)
        );
      } else if (type === 'personal-info') {
        const corrections: Array<{ bureau: Bureau; category: string; value: string }> = req.corrections || [];
        const bureauMap: Record<string, Bureau[]> = {};
        corrections.forEach(c => {
          if (!bureauMap[c.value]) bureauMap[c.value] = [];
          bureauMap[c.value].push(c.bureau);
        });
        setSelectedPersonalInfoBureaus(bureauMap);
      }
    };

    // Populate immediately from prop if it has rawRequest
    if (dispute.rawRequest) {
      populateFrom(dispute);
    }

    // Always fetch fresh dispute data from the manager endpoint
    const fetchFreshDispute = async () => {
      try {
        setIsLoadingDispute(true);
        const fresh = await apiService.getManagerDispute(dispute.id);
        setFreshDispute(fresh);
        populateFrom(fresh);
      } catch (err) {
        console.error('[EditDisputeDialog] Failed to fetch dispute:', err);
        // If we didn't populate from prop, try prop as fallback
        if (!dispute.rawRequest) {
          populateFrom(dispute);
        }
      } finally {
        if (!cancelled) setIsLoadingDispute(false);
      }
    };
    fetchFreshDispute();

    return () => { cancelled = true; };
  }, [isOpen, dispute.id, accounts, availableInquiries]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(''), 10000);
      return () => clearTimeout(t);
    }
  }, [error]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showAccountInfo) {
        const target = event.target as HTMLElement;
        if (!target.closest('.info-popup') && !target.closest('.info-button')) {
          setShowAccountInfo(null);
        }
      }
    };
    if (showAccountInfo) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAccountInfo]);

  const handleAccountSelect = (accountId: string) => {
    if (expandedAccountId === accountId) {
      setExpandedAccountId(null);
    } else {
      setExpandedAccountId(accountId);
      if (!selectedBureaus[accountId]) {
        setSelectedBureaus(prev => ({
          ...prev,
          [accountId]: [Bureau.TRANSUNION, Bureau.EXPERIAN, Bureau.EQUIFAX],
        }));
      }
    }
    setSelectedAccountId(accountId);
  };

  const toggleBureauSelection = (accountId: string, bureau: Bureau) => {
    setSelectedBureaus(prev => {
      const current = prev[accountId] || [];
      return {
        ...prev,
        [accountId]: current.includes(bureau)
          ? current.filter(b => b !== bureau)
          : [...current, bureau],
      };
    });
  };

  const togglePersonalInfoBureauSelection = (field: string, bureau: Bureau) => {
    setSelectedPersonalInfoBureaus(prev => {
      const current = prev[field] || [];
      return {
        ...prev,
        [field]: current.includes(bureau)
          ? current.filter(b => b !== bureau)
          : [...current, bureau],
      };
    });
  };

  const handlePersonalInfoFieldSelect = (field: string, bureauValues: { tu: string; ex: string; eq: string }) => {
    if (expandedPersonalInfoField === field) {
      setExpandedPersonalInfoField(null);
    } else {
      setExpandedPersonalInfoField(field);
      if (!selectedPersonalInfoBureaus[field]) {
        const available: Bureau[] = [];
        if (bureauValues.tu !== '-') available.push(Bureau.TRANSUNION);
        if (bureauValues.ex !== '-') available.push(Bureau.EXPERIAN);
        if (bureauValues.eq !== '-') available.push(Bureau.EQUIFAX);
        setSelectedPersonalInfoBureaus(prev => ({ ...prev, [field]: available }));
      }
    }
  };

  // Personal info fields builder (same logic as CreateDisputeDialog)
  const buildPersonalInfoFields = () => {
    const personalInfoByBureau: { [key: string]: PersonalInformation } = {};
    personalInformation.forEach(info => {
      const b = info.bureau.toLowerCase();
      if (b.includes('transunion') || b.includes('tu')) personalInfoByBureau['TransUnion'] = info;
      else if (b.includes('experian') || b.includes('exp') || b === 'ex') personalInfoByBureau['Experian'] = info;
      else if (b.includes('equifax') || b.includes('eq')) personalInfoByBureau['Equifax'] = info;
    });

    const getBureauData = (field: keyof PersonalInformation) => {
      const v = (val: any) => (!val || val === '' || (Array.isArray(val) && val.length === 0) ? '-' : val);
      return {
        tu: v(personalInfoByBureau['TransUnion']?.[field]),
        ex: v(personalInfoByBureau['Experian']?.[field]),
        eq: v(personalInfoByBureau['Equifax']?.[field]),
      };
    };

    const getFieldStatus = (field: keyof PersonalInformation) => {
      const data = getBureauData(field);
      const values = [data.tu, data.ex, data.eq].filter(v => v !== '-');
      if (values.length === 0) return { label: 'Match', color: 'green' as const };
      const unique = Array.from(new Set(values));
      if (unique.length === 1) return { label: 'Match', color: 'green' as const };
      if (unique.length === values.length) return { label: 'Mismatch', color: 'red' as const };
      return { label: 'Partial Match', color: 'orange' as const };
    };

    const hasAnyData = (bv: { tu: string; ex: string; eq: string }) => bv.tu !== '-' || bv.ex !== '-' || bv.eq !== '-';

    const fields: Array<{
      field: string;
      key: keyof PersonalInformation;
      status: string;
      statusColor: 'red' | 'orange' | 'green';
      bureauValues: { tu: string; ex: string; eq: string };
    }> = [];

    const nameData = getBureauData('name');
    if (hasAnyData(nameData)) { const s = getFieldStatus('name'); fields.push({ field: 'Full Name', key: 'name', status: s.label, statusColor: s.color, bureauValues: nameData }); }
    const dobData = getBureauData('dob');
    if (hasAnyData(dobData)) { const s = getFieldStatus('dob'); fields.push({ field: 'Date of Birth', key: 'dob', status: s.label, statusColor: s.color, bureauValues: dobData }); }
    const addrData = getBureauData('currentAddress');
    if (hasAnyData(addrData)) { const s = getFieldStatus('currentAddress'); fields.push({ field: 'Current Address', key: 'currentAddress', status: s.label, statusColor: s.color, bureauValues: addrData }); }

    const maxPrev = Math.max(
      personalInfoByBureau['TransUnion']?.previousAddresses?.length || 0,
      personalInfoByBureau['Experian']?.previousAddresses?.length || 0,
      personalInfoByBureau['Equifax']?.previousAddresses?.length || 0
    );
    for (let i = 0; i < maxPrev; i++) {
      const d = {
        tu: personalInfoByBureau['TransUnion']?.previousAddresses?.[i] || '-',
        ex: personalInfoByBureau['Experian']?.previousAddresses?.[i] || '-',
        eq: personalInfoByBureau['Equifax']?.previousAddresses?.[i] || '-',
      };
      if (!hasAnyData(d)) continue;
      const vals = [d.tu, d.ex, d.eq].filter(v => v !== '-');
      const uniq = Array.from(new Set(vals));
      const st = uniq.length === 1 ? { label: 'Match', color: 'green' as const } : uniq.length === vals.length ? { label: 'Mismatch', color: 'red' as const } : { label: 'Partial Match', color: 'orange' as const };
      fields.push({ field: `Previous Address ${i + 1}`, key: 'previousAddresses', status: st.label, statusColor: st.color, bureauValues: d });
    }

    const maxEmp = Math.max(
      personalInfoByBureau['TransUnion']?.employmentHistory?.length || 0,
      personalInfoByBureau['Experian']?.employmentHistory?.length || 0,
      personalInfoByBureau['Equifax']?.employmentHistory?.length || 0
    );
    for (let i = 0; i < maxEmp; i++) {
      const d = {
        tu: personalInfoByBureau['TransUnion']?.employmentHistory?.[i]?.employer || '-',
        ex: personalInfoByBureau['Experian']?.employmentHistory?.[i]?.employer || '-',
        eq: personalInfoByBureau['Equifax']?.employmentHistory?.[i]?.employer || '-',
      };
      if (!hasAnyData(d)) continue;
      fields.push({ field: `Employer ${i + 1}`, key: 'employmentHistory', status: 'Partial Match', statusColor: 'orange', bureauValues: d });
    }

    return fields;
  };

  const personalInfoFields = disputeType === 'personal-info' ? buildPersonalInfoFields() : [];

  // Build the account data object from analytics or fall back to rawRequest
  const getAccountData = (accountId: string) => {
    const analyticsAccount = accounts.find(a => a.id === accountId);
    if (analyticsAccount) {
      return {
        id: accountId,
        creditorName: analyticsAccount.creditorName,
        accountNumber: analyticsAccount.accountNumber,
        accountType: analyticsAccount.accountType,
        dateOpened: analyticsAccount.dateOpened,
        status: analyticsAccount.accountStatus,
        balance: analyticsAccount.currentBalance,
        address: analyticsAccount.address,
      };
    }
    // Fall back to raw request data
    const rawDisputes = (dispute.rawRequest?.bureauAccountDisputes || []) as Array<{ account: Record<string, any> }>;
    const rawAccount = rawDisputes[0]?.account;
    if (rawAccount) {
      return {
        id: rawAccount.id || accountId,
        creditorName: rawAccount.creditorName || '',
        accountNumber: rawAccount.accountNumber || '',
        accountType: rawAccount.accountType || '',
        dateOpened: rawAccount.dateOpened || '',
        status: rawAccount.status || '',
        balance: rawAccount.balance || '',
        address: rawAccount.address,
      };
    }
    return null;
  };

  const buildEditPayload = (): EditDisputeRequest => {
    const payload: EditDisputeRequest = {};
    const activeDispute = freshDispute || dispute;
    const req = activeDispute.rawRequest || {};

    if (reason !== (req.reason || '')) payload.reason = reason;
    if (supportingReason !== (req.supportingReason || activeDispute.supportingReason || '')) payload.supportingReason = supportingReason;

    const origDocs = req.attachmentNames || activeDispute.attachmentNames || [];
    if (JSON.stringify([...selectedDocuments].sort()) !== JSON.stringify([...origDocs].sort())) {
      payload.attachmentNames = selectedDocuments;
    }

    if (disputeType === 'account') {
      const accountData = getAccountData(selectedAccountId);
      if (accountData) {
        const bureauList = selectedBureaus[selectedAccountId] || [Bureau.EQUIFAX, Bureau.EXPERIAN, Bureau.TRANSUNION];
        payload.bureauAccountDisputes = bureauList.map(bureau => ({
          bureau,
          account: accountData,
        }));
      }
    } else if (disputeType === 'inquiry') {
      const selected = availableInquiries.filter(inq => selectedInquiryIds.includes(inq.id));
      if (selected.length > 0) {
        payload.bureauInquiryDisputes = selected.map(inq => ({
          bureau: inq.bureau as Bureau,
          inquiry: { id: inq.id, date: inq.date, company: inq.company },
        }));
      } else {
        // Fall back to rawRequest inquiry data for IDs not found in analytics
        const rawInqDisputes = (req.bureauInquiryDisputes || []) as Array<{ bureau: string; inquiry: { id: string; date: string; company: string } }>;
        const matched = rawInqDisputes.filter(bd => selectedInquiryIds.includes(bd.inquiry.id));
        if (matched.length > 0) {
          payload.bureauInquiryDisputes = matched.map(bd => ({
            bureau: bd.bureau as Bureau,
            inquiry: { id: bd.inquiry.id, date: bd.inquiry.date, company: bd.inquiry.company },
          }));
        }
      }
    } else if (disputeType === 'late') {
      const accountData = getAccountData(selectedAccountId);
      if (accountData) {
        const bureauList = selectedBureaus[selectedAccountId] || [Bureau.EQUIFAX, Bureau.EXPERIAN, Bureau.TRANSUNION];
        payload.bureauLatesDisputes = bureauList.map(bureau => ({
          bureau,
          account: accountData,
        }));
      }
    } else if (disputeType === 'personal-info') {
      const corrections: Array<{ bureau: Bureau; category: PersonalInfoCategory; value: string }> = [];
      Object.entries(selectedPersonalInfoBureaus).forEach(([field, bureaus]) => {
        if (bureaus.length === 0) return;
        const category: PersonalInfoCategory = field.includes('Address') ? 'Address'
          : field.includes('Name') ? 'Name'
          : field.includes('Birth') ? 'DOB'
          : field.includes('Employer') ? 'Employment'
          : 'Name';
        bureaus.forEach(bureau => corrections.push({ bureau, category, value: field }));
      });
      payload.corrections = corrections;
    }

    return payload;
  };

  const isSaveDisabled = () => {
    if (isSaving) return true;
    if (disputeType === 'personal-info') {
      return !Object.values(selectedPersonalInfoBureaus).some(b => b.length > 0);
    }
    if (disputeType === 'account' || disputeType === 'late') return !selectedAccountId;
    if (disputeType === 'inquiry') return selectedInquiryIds.length === 0;
    return false;
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError('');
      const payload = buildEditPayload();

      if (Object.keys(payload).length === 0) {
        setError('No changes detected.');
        setIsSaving(false);
        return;
      }

      const updated = await apiService.editDispute(dispute.id, payload);
      onDisputeUpdated(updated);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update dispute');
    } finally {
      setIsSaving(false);
    }
  };

  // Account reason options (same as create)
  const accountReasons = [
    { value: 'I want dispute the accuracy of this account', label: 'I want to dispute the accuracy of this account', description: 'Account information appears to be inaccurate, and I request a review' },
    { value: "This isn't my account or I never authorized it", label: "This isn't my account or I never authorized it", description: "Account doesn't belong to you or was opened without permission." },
    { value: 'This negative account was listed twice or re-aged to look recent', label: 'This negative account was listed twice or re-aged to look recent', description: 'The same delinquency appears multiple times or shows a new start date.' },
    { value: "The creditor couldn't confirm this information during investigation", label: "The creditor couldn't confirm this information during investigation", description: 'The CRA or furnisher failed to provide proof within 30 days.' },
    { value: 'I actually paid on time, the late mark or charge-off is a mistake', label: 'I actually paid on time, the late mark or charge-off is a mistake', description: 'The creditor misreported an on-time or settled account as late or delinquent.' },
    { value: 'The date, balance, or payment status is incorrect', label: 'The date, balance, or payment status is incorrect', description: "Payment dates, amounts, or current status don't match your records." },
    { value: 'This belongs to someone else or was opened using my personal info', label: 'This belongs to someone else or was opened using my personal info', description: "Someone else's account appears in your report or identity theft occurred." },
    { value: 'This account is over seven years old and should be removed', label: 'This account is over seven years old and should be removed', description: 'The delinquency date exceeds seven years (except certain bankruptcies).' },
    { value: 'This account was discharged or settled', label: 'This account was discharged or settled', description: 'An account still shows negative marks after a bankruptcy discharge.' },
  ];

  const inquiryReasons = [
    { value: 'You never applied for credit or authorized the company', label: 'You never applied for credit or authorized the company', description: "I didn't give permission for this inquiry" },
    { value: 'This inquiry was made by mistake or for the wrong reason', label: 'This inquiry was made by mistake or for the wrong reason', description: 'A company pulled your report without a valid purpose' },
    { value: 'The same application caused multiple inquiries', label: 'The same application caused multiple inquiries', description: 'This inquiry shows up more than once' },
    { value: "I already disputed this, but it wasn't fixed", label: "I already disputed this, but it wasn't fixed", description: "The bureau or lender didn't remove the inquiry after your first dispute" },
    { value: 'I just want to confirm this inquiry is correct', label: 'I just want to confirm this inquiry is correct', description: 'You recognize the company but want verification and minimal impact' },
  ];

  const lateReasons = [
    { value: 'The late payment is wrong or never happened', label: 'The late payment is wrong or never happened', description: 'The payment was on time or misreported.' },
    { value: 'The account shows a late payment I already fixed or paid', label: 'The account shows a late payment I already fixed or paid', description: 'The payment issue was resolved but still appears on your report.' },
    { value: 'This account was affected by a system or posting delay', label: 'This account was affected by a system or posting delay', description: 'The creditor posted payment late even though it was made on time.' },
    { value: 'The late payment looks duplicated or dated incorrectly', label: 'The late payment looks duplicated or dated incorrectly', description: 'The same late mark appears more than once or was re-aged to look newer.' },
    { value: 'This is not my account or I was a victim of fraud', label: 'This is not my account or I was a victim of fraud', description: "The account doesn't belong to you or was opened without permission." },
    { value: "I've disputed this before but it wasn't corrected", label: "I've disputed this before but it wasn't corrected", description: 'A previous correction request was ignored or mishandled.' },
  ];

  const reasons = disputeType === 'inquiry' ? inquiryReasons : disputeType === 'late' ? lateReasons : accountReasons;

  const renderReasonSection = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">2</div>
        <h3 className="text-base font-semibold">Select Dispute Reason</h3>
      </div>
      <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
        <div className="space-y-2">
          {reasons.map((option) => {
            const isSelected = reason === option.value;
            return (
              <div
                key={option.value}
                onClick={() => setReason(option.value)}
                className={`border cursor-pointer transition-all ${isSelected ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}
                style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr', alignItems: 'center', padding: '0 14px' }}
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-full border border-gray-300 flex items-center justify-center flex-shrink-0" style={{ width: '16px', height: '16px' }}>
                    {isSelected && <div className="rounded-full bg-black" style={{ width: '8px', height: '8px' }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{option.label}</div>
                    <div className="text-xs text-gray-600 mt-1">{option.description}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div>
        <label className="text-sm font-medium text-gray-700">Supporting Reason (Optional)</label>
        <textarea
          className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
          rows={3}
          placeholder="Provide any additional information about your dispute"
          value={supportingReason}
          onChange={(e) => setSupportingReason(e.target.value)}
        />
      </div>
    </div>
  );

  const renderDocumentsSection = (stepNum: number) => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">{stepNum}</div>
        <h3 className="text-base font-semibold">Select Required Documents</h3>
      </div>
      <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
        <div className="space-y-2">
          {documents.length === 0 ? (
            <div className="text-sm text-gray-500 py-8 text-center">No documents available</div>
          ) : (
            documents.map((doc) => {
              const isSelected = selectedDocuments.includes(doc.documentName);
              return (
                <div
                  key={doc.id}
                  onClick={() => {
                    if (isSelected) setSelectedDocuments(selectedDocuments.filter(n => n !== doc.documentName));
                    else setSelectedDocuments([...selectedDocuments, doc.documentName]);
                  }}
                  className={`border cursor-pointer transition-all ${isSelected ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}
                  style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr', alignItems: 'center', padding: '0 14px' }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`border flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-black bg-black' : 'border-gray-300'}`} style={{ width: '14px', height: '14px', borderRadius: '3px' }}>
                      {isSelected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{doc.documentName}</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {(doc.fileSize / 1024).toFixed(0)} KB • {new Date(doc.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );

  const renderAccountSelector = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">1</div>
        <h3 className="text-base font-semibold">Select Accounts to Dispute</h3>
      </div>
      <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
        <div className="space-y-2">
          {accounts.length === 0 ? (
            <div className="text-sm text-gray-500 py-8 text-center">No accounts available</div>
          ) : (
            accounts.map((account) => {
              const isExpanded = expandedAccountId === account.id;
              const accountBureaus = selectedBureaus[account.id] || [];
              return (
                <div key={account.id} className={`border transition-all ${isExpanded ? 'border-black' : 'border-gray-200'}`} style={{ borderRadius: '8px' }}>
                  <div
                    onClick={() => handleAccountSelect(account.id)}
                    className={`cursor-pointer transition-all ${isExpanded ? 'border-b border-gray-200' : 'hover:bg-gray-50'}`}
                    style={{ height: '60px', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', padding: '0 14px', gap: '16px' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="rounded-full border border-gray-300 flex items-center justify-center flex-shrink-0" style={{ width: '16px', height: '16px' }}>
                        {isExpanded && <div className="rounded-full bg-black" style={{ width: '8px', height: '8px' }} />}
                      </div>
                      <div className="flex-shrink-0 rounded-full bg-gray-100 flex items-center justify-center" style={{ width: '32px', height: '32px' }}>
                        <CreditCard className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-sm">{account.creditorName}</div>
                        <div className="text-xs text-gray-600 mt-1">{account.accountNumber}</div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2">
                      {account.accountStatus === 'Open' && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 border border-green-600" style={{ borderRadius: '8px' }}>Open</span>
                      )}
                      {account.accountStatus === 'Closed' && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 border border-gray-600" style={{ borderRadius: '8px' }}>Closed</span>
                      )}
                      <div className="relative">
                        <button
                          type="button"
                          ref={(el) => { infoButtonRefs.current[account.id] = el; }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (showAccountInfo === account.id) { setShowAccountInfo(null); setPopupPosition(null); }
                            else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setPopupPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
                              setShowAccountInfo(account.id);
                            }
                          }}
                          className="info-button flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-100 transition-colors"
                        >
                          <Info className="h-[18px] w-[18px] text-gray-500" />
                        </button>
                        {showAccountInfo === account.id && popupPosition && createPortal(
                          <div
                            className="info-popup fixed z-[9999] w-[280px] bg-white border border-gray-200 shadow-lg p-4 space-y-3"
                            style={{ borderRadius: '8px', top: `${popupPosition.top}px`, right: `${popupPosition.right}px` }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                              <div><div className="text-xs text-gray-500">Creditor Name</div><div className="text-sm text-gray-900">{account.creditorName}</div></div>
                              <div><div className="text-xs text-gray-500">Date Opened</div><div className="text-sm text-gray-900">{account.dateOpened || 'N/A'}</div></div>
                              <div><div className="text-xs text-gray-500">Account Type</div><div className="text-sm text-gray-900">{account.accountType || 'N/A'}</div></div>
                              <div><div className="text-xs text-gray-500">Status</div><div className="text-sm text-gray-900">{account.accountStatus || 'N/A'}</div></div>
                              <div><div className="text-xs text-gray-500">Account Number</div><div className="text-sm text-gray-900">{account.accountNumber}</div></div>
                              <div><div className="text-xs text-gray-500">Balance</div><div className="text-sm text-gray-900">{account.currentBalance || '$0.00'}</div></div>
                            </div>
                          </div>,
                          document.body
                        )}
                      </div>
                    </div>
                  </div>
                  {isExpanded && (() => {
                    const bureauVariants = accountsGroupedById.get(account.id) || [];
                    const tuAccount = bureauVariants.find(acc => acc.bureau === 'TransUnion');
                    const exAccount = bureauVariants.find(acc => acc.bureau === 'Experian');
                    const eqAccount = bureauVariants.find(acc => acc.bureau === 'Equifax');

                    return (
                    <div className="px-3 pt-2 pb-2 space-y-1">
                      {([
                        { bureau: Bureau.TRANSUNION, label: 'TU', bureauAccount: tuAccount },
                        { bureau: Bureau.EXPERIAN, label: 'EX', bureauAccount: exAccount },
                        { bureau: Bureau.EQUIFAX, label: 'EQ', bureauAccount: eqAccount },
                      ] as const).map(({ bureau, label, bureauAccount }) => {
                        const isSel = accountBureaus.includes(bureau);
                        const status = bureauAccount?.accountStatus || account.accountStatus;
                        const balance = bureauAccount?.currentBalance || account.currentBalance;
                        return (
                          <div
                            key={bureau}
                            onClick={(e) => { e.stopPropagation(); toggleBureauSelection(account.id, bureau); }}
                            className={`cursor-pointer transition-all ${isSel ? 'bg-gray-50' : 'bg-white'}`}
                            style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', padding: '0 14px', gap: '16px' }}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`border flex items-center justify-center flex-shrink-0 ${isSel ? 'border-black bg-black' : 'border-gray-300'}`} style={{ width: '18px', height: '18px', borderRadius: '5px' }}>
                                {isSel && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                              </div>
                              <div className="flex items-center justify-center flex-shrink-0 bg-white border border-gray-300 text-gray-700 text-xs font-semibold" style={{ width: '32px', height: '24px', borderRadius: '6px' }}>
                                {label}
                              </div>
                              <div className="min-w-0">
                                <div className="font-medium text-sm">{account.creditorName}</div>
                                <div className="text-xs text-gray-600 mt-1">{status} • {balance || '$0.00'}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    );
                  })()}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );

  const renderInquirySelector = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">1</div>
        <h3 className="text-base font-semibold">Select Inquiries to Dispute</h3>
      </div>
      <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
        <div className="space-y-2">
          {availableInquiries.length === 0 ? (
            <div className="text-sm text-gray-500 py-8 text-center">No inquiries available</div>
          ) : (
            availableInquiries.map((inquiry) => {
              const isSelected = selectedInquiryIds.includes(inquiry.id);
              return (
                <div
                  key={inquiry.id}
                  onClick={() => {
                    if (isSelected) setSelectedInquiryIds(selectedInquiryIds.filter(id => id !== inquiry.id));
                    else setSelectedInquiryIds([...selectedInquiryIds, inquiry.id]);
                  }}
                  className={`border cursor-pointer transition-all ${isSelected ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}
                  style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', padding: '0 14px', gap: '16px' }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`border flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-black bg-black' : 'border-gray-300'}`} style={{ width: '14px', height: '14px', borderRadius: '3px' }}>
                      {isSelected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{inquiry.company}</div>
                      <div className="text-xs text-gray-600 mt-1">Date: {inquiry.date}</div>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800" style={{ borderRadius: '8px' }}>
                    {inquiry.bureau}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );

  const renderPersonalInfoSelector = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">1</div>
        <h3 className="text-base font-semibold">Select Personal Information to Dispute</h3>
      </div>
      <div className="border border-gray-200 max-h-[420px] overflow-y-auto p-4" style={{ borderRadius: '8px' }}>
        <div className="space-y-2">
          {personalInfoFields.length === 0 ? (
            <div className="text-sm text-gray-500 py-8 text-center">No personal information available</div>
          ) : (
            personalInfoFields.map((item, index) => {
              const isExpanded = expandedPersonalInfoField === item.field;
              const fieldBureaus = selectedPersonalInfoBureaus[item.field] || [];
              return (
                <div key={index} className={`border transition-all ${isExpanded ? 'border-black' : 'border-gray-200'}`} style={{ borderRadius: '8px' }}>
                  <div
                    onClick={() => handlePersonalInfoFieldSelect(item.field, item.bureauValues)}
                    className={`cursor-pointer transition-all ${isExpanded ? 'border-b border-gray-200' : 'hover:bg-gray-50'}`}
                    style={{ height: '60px', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', padding: '0 14px', gap: '16px' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="rounded-full border border-gray-300 flex items-center justify-center flex-shrink-0" style={{ width: '16px', height: '16px' }}>
                        {isExpanded && <div className="rounded-full bg-black" style={{ width: '8px', height: '8px' }} />}
                      </div>
                      <div className="min-w-0"><div className="font-medium text-sm">{item.field}</div></div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium ${
                      item.statusColor === 'red' ? 'text-red-600 border border-red-600' :
                      item.statusColor === 'orange' ? 'text-orange-600 border border-orange-600' :
                      'text-green-600 border border-green-600'
                    }`} style={{ borderRadius: '8px' }}>
                      {item.status}
                    </span>
                  </div>
                  {isExpanded && (
                    <div className="px-3 pt-2 pb-2 space-y-1">
                      {([
                        { bureau: Bureau.TRANSUNION, label: 'TU', valueKey: 'tu' as const },
                        { bureau: Bureau.EXPERIAN, label: 'EX', valueKey: 'ex' as const },
                        { bureau: Bureau.EQUIFAX, label: 'EQ', valueKey: 'eq' as const },
                      ]).filter(({ valueKey }) => item.bureauValues[valueKey] !== '-').map(({ bureau, label, valueKey }) => {
                        const isSel = fieldBureaus.includes(bureau);
                        return (
                          <div
                            key={bureau}
                            onClick={(e) => { e.stopPropagation(); togglePersonalInfoBureauSelection(item.field, bureau); }}
                            className={`cursor-pointer transition-all ${isSel ? 'bg-gray-50' : 'bg-white'}`}
                            style={{ borderRadius: '8px', height: '60px', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', padding: '0 14px', gap: '16px' }}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`border flex items-center justify-center flex-shrink-0 ${isSel ? 'border-black bg-black' : 'border-gray-300'}`} style={{ width: '14px', height: '14px', borderRadius: '3px' }}>
                                {isSel && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                              </div>
                              <div className="flex items-center justify-center flex-shrink-0 bg-white border border-gray-300 text-gray-700 text-xs font-semibold" style={{ width: '32px', height: '24px', borderRadius: '6px' }}>
                                {label}
                              </div>
                              <div className="min-w-0"><div className="font-medium text-sm">{item.bureauValues[valueKey]}</div></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );

  const typeLabel = disputeType === 'account' ? 'Account' : disputeType === 'inquiry' ? 'Inquiry' : disputeType === 'late' ? 'Late Payments' : 'Personal Info';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="!h-screen !max-w-none !translate-x-0 !translate-y-0 !top-0 !left-auto !right-0 !rounded-none !data-[state=closed]:slide-out-to-right !data-[state=open]:slide-in-from-right !flex !flex-col !justify-start !items-start !p-0 !gap-0 [&>button]:!right-6 [&>button]:!top-6"
        style={{ width: '840px' }}
      >
        <DialogHeader
          className="flex-shrink-0 w-full space-y-0"
          style={{ paddingLeft: '24px', paddingRight: '72px', paddingTop: '24px', paddingBottom: '24px' }}
        >
          <DialogTitle className="text-lg font-semibold flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            Edit Dispute
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            Type: <span className="font-medium text-gray-700">{typeLabel}</span>
          </p>
        </DialogHeader>

        <div className="pt-2 pb-4 px-6 w-full flex-1 overflow-y-auto">
          {isLoadingDispute && (
            <div className="flex items-center gap-2 px-1 pb-3 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading latest dispute data...
            </div>
          )}
          <div className="space-y-6">
            {/* Section 1: Item selection */}
            {(disputeType === 'account' || disputeType === 'late') && renderAccountSelector()}
            {disputeType === 'inquiry' && renderInquirySelector()}
            {disputeType === 'personal-info' && renderPersonalInfoSelector()}

            {/* Section 2: Reason (not for personal-info which has its own inline reason) */}
            {disputeType !== 'personal-info' && renderReasonSection()}

            {/* Personal Info reason */}
            {disputeType === 'personal-info' && Object.values(selectedPersonalInfoBureaus).some(b => b.length > 0) && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold">2</div>
                  <h3 className="text-base font-semibold">Provide Dispute Reason</h3>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Dispute Reason</label>
                  <textarea
                    className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                    rows={4}
                    placeholder="Provide reason about your dispute"
                    value={supportingReason}
                    onChange={(e) => setSupportingReason(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Section 3: Documents */}
            {disputeType !== 'personal-info' && renderDocumentsSection(3)}
          </div>
        </div>

        {/* Bottom Action Bar */}
        <div className="w-full bg-white border-t border-gray-200">
          <div className="px-6 py-2 flex items-center justify-between gap-4">
            {error ? (
              <p className="text-sm text-red-600 flex-1">{error}</p>
            ) : isSaving ? (
              <p className="text-sm text-gray-500 flex-1">Regenerating letters... This may take up to 15 seconds.</p>
            ) : (
              <div />
            )}
            <Button
              onClick={handleSave}
              disabled={isSaveDisabled()}
              className="h-10 px-6 bg-gray-800 hover:bg-gray-900 text-white font-medium flex-shrink-0"
              style={{ borderRadius: '8px' }}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
